import { isPlatformServer } from '@angular/common';
import { Component, ChangeDetectionStrategy, Input, OnInit, PLATFORM_ID, ApplicationRef, ChangeDetectorRef, DestroyRef, inject, ElementRef } from '@angular/core';
import { BehaviorSubject, Subject, filter, first, fromEvent, map, merge, repeat, switchMap, timer } from 'rxjs';
import { BreakpointObserver } from '@angular/cdk/layout';
import { HeroCarouselIntl } from './hero-carousel.intl';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AspectRatio, Breakpoint, DEVICE_TYPE_SERVICE_TOKEN, greater } from '@knorr-bremse-portals/ngx-components';
import { HeroItem, HeroItemComponent, isHeroPicture } from '@knorr-bremse-portals/ngx-components/components/hero/hero-item';
import { IconComponent } from '@knorr-bremse-portals/ngx-components/components/icon';
import { AbstractMode } from '@knorr-bremse-portals/ngx-components/components/overlay-figure';
import { iconArrowLeft, iconArrowRight } from '@knorr-bremse-portals/icons';

export type HeroCarouselPage = HeroItem[] | HeroItem;

@Component({
  selector: 'kb-hero-carousel',
  templateUrl: './hero-carousel.component.html',
  styleUrls: ['./hero-carousel.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [IconComponent, HeroItemComponent],
})
export class HeroCarouselComponent implements OnInit {

  /** The array of times is used per page, if the number of pages exceeds the number of items in the array, the last item is used */
  @Input() public pageTimes: number[] = [8000, 5000]; // TODO: move the time to a central place

  @Input({ required: true }) public pages!: HeroCarouselPage[];

  @Input() public abstractMode: AbstractMode = 'visible';

  protected iconArrowLeft = iconArrowLeft;
  protected iconArrowRight = iconArrowRight;

  private hostElementRef = inject(ElementRef);
  private destroyRef = inject(DestroyRef);
  private appRef = inject(ApplicationRef);
  private cdr = inject(ChangeDetectorRef);
  private platformId = inject(PLATFORM_ID);
  protected intl = inject(HeroCarouselIntl);
  private breakpointObserver = inject(BreakpointObserver);
  protected isMobile = inject(DEVICE_TYPE_SERVICE_TOKEN).isMobile() === true;

  public constructor() {
    this.intl.changes$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.cdr.markForCheck());
  }

  public ngOnInit(): void {
    this.initAspectRatios();
    this.initAutomaticNavigation();
    this.initFocusHandler();
  }

  private initAspectRatios() {
    for(const page of this.pages) {
      if (this.isMultiItemPage(page)) {
        for(const item of page) {
          if (isHeroPicture(item)) {
            item.variants = [{ aspectRatio: AspectRatio.Standard }];
          }
        }
      }
    }
  }

  private hover$ = new BehaviorSubject(false);
  protected enableHover(): void {
    this.hover$.next(true);
  }
  protected disableHover(): void {
    this.hover$.next(false);
  }

  private windowFocus$ = new BehaviorSubject(true);
  private initFocusHandler(): void {
    if (isPlatformServer(this.platformId)) {
      return;
    }

    merge(
      fromEvent(window, 'focus').pipe(map(() => true)),
      fromEvent(window, 'blur').pipe(map(() => false)),
    ).subscribe((focus) => {
      this.windowFocus$.next(focus);
    })
  }

  private getActivePageTime(): number {
    return this.pageTimes[this.activeItemIndex] ?? this.pageTimes[this.pageTimes.length - 1];
  }

  private resetAutomaticTimer$ = new Subject<void>();

  private initAutomaticNavigation() {
    if (isPlatformServer(this.platformId)) {
      return;
    }

    const visible$ = new BehaviorSubject(false);
    const observer = new IntersectionObserver((entries) => {
      visible$.next(entries[0].isIntersecting);
    }, { threshold: 0.1 });

    observer.observe(this.hostElementRef.nativeElement);
    this.destroyRef.onDestroy(() => {
      observer.disconnect();
    });

    merge(this.hover$, this.windowFocus$, visible$, this.resetAutomaticTimer$).pipe(
      switchMap(() => this.appRef.isStable.pipe(
        first(stable => stable),
        switchMap(() => timer(this.getActivePageTime())),
        filter(() => !this.hover$.value),
        filter(() => this.windowFocus$.value),
        filter(() => visible$.value),
        repeat(),
        takeUntilDestroyed(this.destroyRef),
      )),
    ).subscribe(() => {
      this.openNextPage(true);
      this.cdr.detectChanges();
    });
  }

  protected isMultiItemPage(item: HeroCarouselPage): item is HeroItem[] {
    return Array.isArray(item);
  }

  private getPageSize(page: HeroCarouselPage): number {
    return this.isMultiItemPage(page) ? page.length : 1;
  }

  private getItemCount(): number {
    return this.pages.reduce((count, page) => count + this.getPageSize(page), 0);
  }

  private activeItemIndex = 0;

  protected navigatePageIndex(index: number) {
    this.activeItemIndex = index;
    this.resetAutomaticTimer$.next();
  }

  private navigatePage(next: boolean, automatic: boolean) {
    const itemCount = this.getItemCount();

    let targetIndex;
    if (this.breakpointObserver.isMatched(greater(Breakpoint.MediumSmall))) {
      const activePageIndex = this.getPageIndex(this.activeItemIndex);

      const targetPageIndex = ((activePageIndex + (next ? 1 : -1)) + this.pages.length) % this.pages.length;

      targetIndex = this.getAbsolutePageIndex(targetPageIndex);
    } else {
      targetIndex = ((this.activeItemIndex + (next ? 1 : -1)) + itemCount) % itemCount;
    }
    this.activeItemIndex = targetIndex;
    if (automatic === false) {
      this.resetAutomaticTimer$.next();
    }
  }

  protected openPreviousPage() {
    this.navigatePage(false, false);
  }

  protected openNextPage(automatic = false) {
    this.navigatePage(true, automatic);
  }

  private getPageIndex(itemIndex: number) {
    let pageIndex = 0;
    for (let i = 0; i < this.pages.length; i++) {
      const page = this.pages[i];
      const pageSize = this.getPageSize(page);
      if (itemIndex < pageIndex + pageSize) {
        return i;
      }
      pageIndex += pageSize;
    }
    return pageIndex;
  }

  protected getAbsolutePageIndex(pageIndex: number): number {
    let index = 0;
    for (let i = 0; i < pageIndex; i++) {
      const page = this.pages[i];
      index += this.isMultiItemPage(page) ? page.length : 1;
    }
    return index;
  }

  protected isPageActive(page: HeroCarouselPage, pageIndex: number): boolean {
    const absoluteIndex = this.getAbsolutePageIndex(pageIndex);

    const pageLength = this.isMultiItemPage(page) ? page.length : 1;

    return absoluteIndex <= this.activeItemIndex && this.activeItemIndex < absoluteIndex + pageLength;
  }

  protected getAbsoluteItemIndex(pageIndex: number, itemIndex: number): number {
    return this.getAbsolutePageIndex(pageIndex) + itemIndex;
  }

  protected isItemActive(pageIndex: number, itemIndex: number): boolean {
    return this.getAbsoluteItemIndex(pageIndex, itemIndex) === this.activeItemIndex;
  }

}
